import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ReportGroupBy } from "~/api/opsTypes";
import { useLedgerActions, useLedgerEfficiency, useLedgerReport } from "~/api/queries";
import { compactTokens, formatMs, formatShare } from "~/features/automation/format";
import { Dialog } from "~/foundation/components/Dialog";
import { Spinner } from "~/foundation/components/Spinner";
import { cn } from "~/foundation/utils/cn";

export interface ActionLedgerProps {
  /** The run to break down, or null for "everything this team's agents have done recently". */
  runId: string | null;
  title?: string;
  open: boolean;
  onClose: () => void;
}

const GROUP_BY_OPTIONS: Array<{ value: ReportGroupBy; label: string }> = [
  { value: "tool", label: "Tool" },
  { value: "model", label: "Model" },
  { value: "effort", label: "Thinking effort" },
  { value: "node", label: "Node" },
  { value: "command_family", label: "Command family" },
  { value: "thinker", label: "Thinker" },
  { value: "kind", label: "Kind" },
  { value: "status", label: "Status" },
  // F-7: the server also accepts `actor`, which kms never offered — the audit question the
  // other eight cannot answer ("who spent it").
  { value: "actor", label: "Actor" },
];

function Stat({
  label,
  value,
  alarm,
  hint,
  extra,
}: {
  label: string;
  value: string;
  alarm?: boolean;
  hint?: string;
  extra?: string;
}) {
  return (
    <div className="flex min-w-24 flex-col gap-0.5">
      <span className="text-small text-text-tertiary uppercase tracking-wide">{label}</span>
      <span
        className={cn("text-h3 font-semibold", alarm ? "text-danger" : "text-text")}
        title={hint}
      >
        {value}
        {extra ? <span className="text-small text-danger">{extra}</span> : null}
      </span>
    </div>
  );
}

/**
 * The per-command drill-down for one run — or for `runId: null`, "everything this team's
 * agents have done recently". Never collapses the four token categories into one number: the
 * v5 run was 96.7% cache-read, and a single "tokens" column is exactly what hid the finding.
 */
export function ActionLedger({ runId, title, open, onClose }: ActionLedgerProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"actions" | "grouped" | "nodes">("actions");
  const [groupBy, setGroupBy] = useState<ReportGroupBy>("tool");
  const [failedOnly, setFailedOnly] = useState(false);

  useEffect(() => {
    if (!runId && tab === "nodes") {
      setTab("actions");
    }
  }, [runId, tab]);

  const { data: page, isLoading: actionsLoading } = useLedgerActions(runId, failedOnly);
  const { data: report, isLoading: reportLoading } = useLedgerReport(
    tab === "grouped" ? (runId ?? undefined) : undefined,
    groupBy,
  );
  const { data: efficiency } = useLedgerEfficiency(runId);

  const actions = page?.actions ?? [];
  const runBilled = efficiency?.run.billedTokens ?? 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={title ?? t("Action ledger")}
      closeLabel={t("Close")}
      size="full"
    >
      <div className="flex flex-col gap-3">
        {!runId ? (
          <div className="rounded-sm border-s-4 border-divider bg-bg-secondary px-3 py-2 text-small text-text-secondary">
            {t(
              "Showing every recorded command for this workspace. Per-node figures — amplification, cache-read share, the worst node — need a run to attribute rows to; a locally-executed flow does not register one yet, so they are omitted here rather than shown as zero.",
            )}
          </div>
        ) : null}

        {runId ? (
          <div className="flex flex-wrap gap-6 rounded-md border border-divider bg-bg-secondary px-4 py-3">
            <Stat
              label={t("Amplification")}
              value={efficiency?.run.amplification ? `${efficiency.run.amplification}×` : "—"}
              alarm={(efficiency?.run.amplification ?? 0) >= 40}
              hint={t(
                "Billed tokens divided by tokens of real content consumed. Above roughly 40x means the node is re-reading its own conversation rather than doing work.",
              )}
            />
            <Stat
              label={t("Cache-read share")}
              value={formatShare(efficiency?.runCacheReadShare ?? null)}
              alarm={(efficiency?.runCacheReadShare ?? 0) > 0.9}
              hint={t(
                "The share of the bill that was re-reading the same conversation. Above 90% is context bloat: split the node.",
              )}
            />
            <Stat label={t("Billed")} value={compactTokens(runBilled)} />
            <Stat
              label={t("Content")}
              value={compactTokens(efficiency?.run.contentTokens ?? null)}
            />
            <Stat label={t("Requests")} value={String(efficiency?.run.requests ?? "—")} />
            <Stat label={t("Actions")} value={String(efficiency?.run.actions ?? actions.length)} />
            <Stat
              label={t("Budget")}
              value={
                efficiency?.run.tokenBudget ? compactTokens(efficiency.run.tokenBudget) : t("none")
              }
              extra={efficiency?.run.budgetTrippedAt ? ` · ${t("tripped")}` : undefined}
            />
            {efficiency?.worstNode ? (
              <Stat label={t("Most expensive node")} value={efficiency.worstNode} />
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 border-b border-divider pb-2">
          <button
            type="button"
            onClick={() => setTab("actions")}
            className={cn(
              "rounded-sm px-3 py-1.5 text-body",
              tab === "actions" ? "bg-bg-secondary font-semibold text-text" : "text-text-secondary",
            )}
          >
            {t("Commands")} ({actions.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("grouped")}
            className={cn(
              "rounded-sm px-3 py-1.5 text-body",
              tab === "grouped" ? "bg-bg-secondary font-semibold text-text" : "text-text-secondary",
            )}
          >
            {t("Grouped")}
          </button>
          {runId ? (
            <button
              type="button"
              onClick={() => setTab("nodes")}
              className={cn(
                "rounded-sm px-3 py-1.5 text-body",
                tab === "nodes" ? "bg-bg-secondary font-semibold text-text" : "text-text-secondary",
              )}
            >
              {t("Per node")} ({efficiency?.nodes.length ?? 0})
            </button>
          ) : null}
          <div className="flex-1" />
          {tab === "actions" ? (
            <label className="inline-flex items-center gap-1.5 text-small text-text-secondary">
              <input
                type="checkbox"
                checked={failedOnly}
                onChange={(event) => setFailedOnly(event.target.checked)}
              />
              {t("Failures only")}
            </label>
          ) : null}
          {tab === "grouped" ? (
            <select
              value={groupBy}
              onChange={(event) => setGroupBy(event.target.value as ReportGroupBy)}
              aria-label={t("Group by")}
              className="h-8 rounded-sm border border-input-border bg-bg px-2 text-body text-text"
            >
              {GROUP_BY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
            </select>
          ) : null}
          {(tab === "actions" && actionsLoading) || (tab === "grouped" && reportLoading) ? (
            <Spinner size={14} />
          ) : null}
        </div>

        <div className="max-h-[55vh] overflow-auto">
          {tab === "actions" ? (
            <table className="w-full text-start text-small">
              <thead>
                <tr className="border-b border-divider text-text-tertiary">
                  <th className="px-2 py-1.5 text-start">#</th>
                  <th className="px-2 py-1.5 text-start">{t("What it did")}</th>
                  <th className="px-2 py-1.5 text-start">{t("Tool")}</th>
                  <th className="px-2 py-1.5 text-start">{t("Model")}</th>
                  <th className="px-2 py-1.5 text-start">{t("Effort")}</th>
                  <th className="px-2 py-1.5 text-end" title={t("Fresh input tokens")}>
                    {t("in")}
                  </th>
                  <th className="px-2 py-1.5 text-end" title={t("Cache write")}>
                    {t("cw")}
                  </th>
                  <th className="px-2 py-1.5 text-end" title={t("Cache read — context re-reads")}>
                    {t("cr")}
                  </th>
                  <th className="px-2 py-1.5 text-end" title={t("Output tokens")}>
                    {t("out")}
                  </th>
                  <th className="px-2 py-1.5 text-end">{t("Total")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Took")}</th>
                </tr>
              </thead>
              <tbody>
                {actions.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-2 py-4 text-text-tertiary">
                      {actionsLoading
                        ? t("Loading the ledger…")
                        : t(
                            "No actions recorded for this run. The extension ingests them as the run works; a run that predates the ledger has none.",
                          )}
                    </td>
                  </tr>
                ) : (
                  actions.map((action) => (
                    <tr key={action.id} className="border-b border-divider last:border-b-0">
                      <td className="px-2 py-1.5 text-text-tertiary tabular-nums">{action.seq}</td>
                      <td className="px-2 py-1.5">
                        <div
                          className={cn(
                            action.summaryGeneric ? "text-text-secondary italic" : "text-text",
                          )}
                        >
                          {action.summary || action.toolName || action.kind}
                        </div>
                        {action.error ? (
                          <div className="mt-0.5 text-small text-danger">{action.error}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-1.5 text-text-secondary">{action.toolName ?? "—"}</td>
                      <td className="px-2 py-1.5 text-text-secondary">{action.model ?? "—"}</td>
                      <td className="px-2 py-1.5 text-text-secondary">{action.effort ?? "—"}</td>
                      <td className="px-2 py-1.5 text-end tabular-nums">
                        {compactTokens(action.tokens.input)}
                      </td>
                      <td className="px-2 py-1.5 text-end tabular-nums">
                        {compactTokens(action.tokens.cacheWrite)}
                      </td>
                      <td className="px-2 py-1.5 text-end text-danger tabular-nums">
                        {compactTokens(action.tokens.cacheRead)}
                      </td>
                      <td className="px-2 py-1.5 text-end tabular-nums">
                        {compactTokens(action.tokens.output)}
                      </td>
                      <td className="px-2 py-1.5 text-end font-semibold tabular-nums">
                        {compactTokens(action.tokens.total)}
                      </td>
                      <td className="px-2 py-1.5 text-end text-text-tertiary tabular-nums">
                        {formatMs(action.durationMs)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : null}

          {tab === "grouped" ? (
            <table className="w-full text-start text-small">
              <thead>
                <tr className="border-b border-divider text-text-tertiary">
                  <th className="px-2 py-1.5 text-start">
                    {t(
                      GROUP_BY_OPTIONS.find((option) => option.value === groupBy)?.label ?? "Tool",
                    )}
                  </th>
                  <th className="px-2 py-1.5 text-end">{t("Calls")}</th>
                  <th className="px-2 py-1.5 text-end">{t("in")}</th>
                  <th className="px-2 py-1.5 text-end">{t("cw")}</th>
                  <th className="px-2 py-1.5 text-end">{t("cr")}</th>
                  <th className="px-2 py-1.5 text-end">{t("out")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Total")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Result bytes")}</th>
                </tr>
              </thead>
              <tbody>
                {(report?.rows ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-2 py-4 text-text-tertiary">
                      {t("Nothing to group yet.")}
                    </td>
                  </tr>
                ) : (
                  (report?.rows ?? []).map((row) => (
                    <tr key={row.key} className="border-b border-divider last:border-b-0">
                      <td className="px-2 py-1.5 font-semibold text-text">
                        {row.key || t("(none)")}
                      </td>
                      <td className="px-2 py-1.5 text-end tabular-nums">{row.calls}</td>
                      <td className="px-2 py-1.5 text-end tabular-nums">
                        {compactTokens(row.inputTokens)}
                      </td>
                      <td className="px-2 py-1.5 text-end tabular-nums">
                        {compactTokens(row.cacheWriteTokens)}
                      </td>
                      <td className="px-2 py-1.5 text-end text-danger tabular-nums">
                        {compactTokens(row.cacheReadTokens)}
                      </td>
                      <td className="px-2 py-1.5 text-end tabular-nums">
                        {compactTokens(row.outputTokens)}
                      </td>
                      <td className="px-2 py-1.5 text-end font-semibold tabular-nums">
                        {compactTokens(row.totalTokens)}
                      </td>
                      <td className="px-2 py-1.5 text-end tabular-nums">
                        {compactTokens(row.resultBytes)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : null}

          {tab === "nodes" ? (
            <table className="w-full text-start text-small">
              <thead>
                <tr className="border-b border-divider text-text-tertiary">
                  <th className="px-2 py-1.5 text-start">{t("Node")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Requests")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Actions")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Files written")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Content")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Billed")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Cache-read share")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Amplification")}</th>
                  <th className="px-2 py-1.5 text-start">{t("Models")}</th>
                  <th className="px-2 py-1.5 text-start">{t("Max effort")}</th>
                </tr>
              </thead>
              <tbody>
                {(efficiency?.nodes ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-2 py-4 text-text-tertiary">
                      {t("No per-node telemetry for this run.")}
                    </td>
                  </tr>
                ) : (
                  (efficiency?.nodes ?? []).map((node) => (
                    <tr
                      key={`${node.index}-${node.nodeId}`}
                      className="border-b border-divider last:border-b-0"
                    >
                      <td className="px-2 py-1.5 font-semibold text-text">{node.nodeId}</td>
                      <td className="px-2 py-1.5 text-end tabular-nums">{node.requests}</td>
                      <td className="px-2 py-1.5 text-end tabular-nums">{node.actions}</td>
                      <td className="px-2 py-1.5 text-end tabular-nums">{node.filesWritten}</td>
                      <td className="px-2 py-1.5 text-end tabular-nums">
                        {compactTokens(node.contentTokens)}
                      </td>
                      <td className="px-2 py-1.5 text-end font-semibold tabular-nums">
                        {compactTokens(node.billedTokens)}
                      </td>
                      <td
                        className={cn(
                          "px-2 py-1.5 text-end tabular-nums",
                          (node.cacheReadShare ?? 0) > 0.9 && "text-danger",
                        )}
                      >
                        {formatShare(node.cacheReadShare)}
                      </td>
                      <td
                        className={cn(
                          "px-2 py-1.5 text-end tabular-nums",
                          (node.amplification ?? 0) >= 40 && "text-danger",
                        )}
                      >
                        {node.amplification ? `${node.amplification}×` : "—"}
                      </td>
                      <td className="px-2 py-1.5 text-text-secondary">
                        {node.models.filter(Boolean).join(", ") || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-text-secondary">{node.effortMax ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
